import { defineStore } from 'pinia'
import type { IReportDescriptionItem } from '@team-milva/lab-models/resources'
import {
  collection,
  doc,
  onSnapshot,
  query,
  updateDoc,
  where,
} from 'firebase/firestore'
import { db } from '@/firebase/init'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { useAccountStore } from '@/stores/accountStore'
import { ReportDescriptionItem } from '@/globals/typescript/models/ReportDescriptionItem'
import type { TLibraryVisibilityFilter } from '@/globals/typescript/utils/libraryVisibilityFilter'

export { ReportDescriptionItem }

type CreateReportDescriptionItemParams = {
  reportDescriptionID: string
  title: string
  text: string
}

type UpdateReportDescriptionItemActiveStateParams = {
  id: string
  reportDescriptionID: string
  activeState: 'active' | 'inactive' | 'archived'
}

export const useReportDescriptionItemStore = defineStore(
  'reportDescriptionItem',
  {
    state: () => ({
      allReportDescriptionItems: [] as ReportDescriptionItem[],
      unsubscribeAllReportDescriptionItems: null as (() => void) | null,
      // Independent of reportDescriptionStore.listStateFilter — the items
      // table inside one description filters on its own, so the two lists
      // never constrain each other. Both lists use the same shared 4-option
      // library visibility filter (active/inactive/archived support).
      listStateFilter: 'active' as TLibraryVisibilityFilter,
    }),

    getters: {
      allReportDescriptionItemsLoaded(): boolean {
        return !!this.unsubscribeAllReportDescriptionItems
      },
    },

    actions: {
      getReportDescriptionItems(): Promise<ReportDescriptionItem[]> {
        if (this.allReportDescriptionItemsLoaded) {
          return Promise.resolve(this.allReportDescriptionItems)
        }

        return new Promise((resolve) => {
          let resolved = false
          const accountStore = useAccountStore()

          const q = query(
            collection(
              db,
              'Apps',
              '-lab-management-',
              'ReportDescriptionItems'
            ),
            where('labAccountID', '==', accountStore.labAccount?.id)
          )

          const unsubscribe = onSnapshot(q, (querySnapshot) => {
            this.allReportDescriptionItems = querySnapshot.docs.map(
              (doc) =>
                new ReportDescriptionItem(doc.data() as IReportDescriptionItem)
            )

            this.unsubscribeAllReportDescriptionItems = unsubscribe

            if (!resolved) {
              resolved = true
              resolve(this.allReportDescriptionItems)
            }
          })
        })
      },

      async createReportDescriptionItem(
        params: CreateReportDescriptionItemParams
      ): Promise<void> {
        const accountStore = useAccountStore()
        const labAccountID = accountStore.labAccount?.id ?? ''

        const result = await managementApi(
          'post',
          'lab-accounts/{labAccountID}/report-descriptions/{reportDescriptionID}/items',
          {
            path: {
              labAccountID,
              reportDescriptionID: params.reportDescriptionID,
            },
            body: {
              title: params.title,
              text: params.text,
            },
          }
        )
        if (!result.success) throw new Error(result.code)
      },

      async updateReportDescriptionItemActiveState(
        params: UpdateReportDescriptionItemActiveStateParams
      ): Promise<void> {
        const accountStore = useAccountStore()
        const labAccountID = accountStore.labAccount?.id ?? ''

        // TODO: remove cast after deploying and re-running openapi:gen — the
        // deployed endpoint's body only accepts 'active'|'inactive' until the
        // widened 'archived' Zod schema (MIL-362, Phase 1) is deployed.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const result = await (managementApi as any)(
          'patch',
          'lab-accounts/{labAccountID}/report-descriptions/{reportDescriptionID}/items/{reportDescriptionItemID}',
          {
            path: {
              labAccountID,
              reportDescriptionID: params.reportDescriptionID,
              reportDescriptionItemID: params.id,
            },
            body: { activeState: params.activeState },
          }
        )
        if (!result.success) throw new Error(result.code)
      },

      async deactivateReportDescriptionItem(
        id: string,
        reportDescriptionID: string
      ): Promise<void> {
        await this.updateReportDescriptionItemActiveState({
          id,
          reportDescriptionID,
          activeState: 'inactive',
        })
      },

      async reactivateReportDescriptionItem(
        id: string,
        reportDescriptionID: string
      ): Promise<void> {
        await this.updateReportDescriptionItemActiveState({
          id,
          reportDescriptionID,
          activeState: 'active',
        })
      },

      async archiveReportDescriptionItem(
        id: string,
        reportDescriptionID: string
      ): Promise<void> {
        await this.updateReportDescriptionItemActiveState({
          id,
          reportDescriptionID,
          activeState: 'archived',
        })
      },

      async updateReportDescriptionItem(
        id: string,
        localeData: Record<string, { title: string; text: string }>
      ): Promise<void> {
        const updates: Record<string, string | number> = {}
        for (const [locale, data] of Object.entries(localeData)) {
          updates[`title.${locale}`] = data.title
          updates[`text.${locale}`] = data.text
        }
        updates['timestamps.updatedAt'] = Date.now()
        await updateDoc(
          doc(db, 'Apps', '-lab-management-', 'ReportDescriptionItems', id),
          updates
        )
      },

      resetStore(): void {
        this.allReportDescriptionItems = []
        this.listStateFilter = 'active'
        if (this.unsubscribeAllReportDescriptionItems) {
          this.unsubscribeAllReportDescriptionItems()
          this.unsubscribeAllReportDescriptionItems = null
        }
      },
    },
  }
)

